<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<nav class="navbar" role="navigation">
  <div class="navbar-brand">
    <div class="zaya-brandwrap" v-click-outside="() => megaOpen = false">
      <button
        class="zaya-brand"
        :class="{open: megaOpen}"
        :aria-expanded="megaOpen ? 'true' : 'false'"
        aria-haspopup="true"
        @click="megaOpen = !megaOpen"
        @keydown.esc="megaOpen = false"
      >
        <img class="zaya-logo" src="@/assets/logo.svg" alt="ZayaAI">
        <span class="zaya-caret"><i class="fas" :class="megaOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i></span>
      </button>
      <div v-if="megaOpen" class="zaya-mega" role="menu">
        <h4>{{ $t('menu') }}</h4>
        <div class="zaya-mega-grid">
          <router-link
            v-for="item in navItems"
            :key="item.id"
            :to="item.to"
            class="zaya-mega-item"
            :class="{active: item.id === activeNavId}"
            role="menuitem"
          >
            <span class="zaya-mega-ico"><i class="fas" :class="item.icon"></i></span>
            <span class="zaya-mega-label">{{ item.label }}</span>
          </router-link>
        </div>
      </div>
    </div>
    <!-- breadcrumb: the current page shown next to the logo (replaces per-page title bars).
         On project pages the section selector lives here (no separate bar below the header);
         on the image viewer the active image filename is appended after the selector. -->
    <div v-if="pageTitle" class="zaya-crumb">
      <span class="zaya-crumb-sep">/</span>
      <span class="zaya-crumb-page">{{ pageTitle }}</span>
      <template v-if="isProjectPage">
        <span class="zaya-crumb-sep">/</span>
        <project-section-selector />
        <template v-if="isViewerPage && activeImage">
          <span class="zaya-crumb-sep">/</span>
          <span class="zaya-crumb-page zaya-crumb-image"><image-name :image="activeImage" /></span>
        </template>
      </template>
    </div>
    <a role="" class="navbar-burger" :class="{'is-active':openedTopMenu}" @click="openedTopMenu=!openedTopMenu">
      <span></span> <span></span> <span></span>
    </a>
  </div>
  <div id="topMenu" class="navbar-menu" :class="{'is-active':openedTopMenu}">
    <div class="navbar-end">
      <ims-searcher />
      <language-selector />
      <!-- TODO IAM -->
      <navbar-dropdown
        :icon="currentUser.adminByNow ? 'fa-star' : 'fa-user'"
        :title="userFirstName"
        :tag="currentUser.adminByNow ? {type: 'is-danger', text: $t('admin')} : null"
        :listPathes="['/account', '/activity']"
        :classes="['is-right']"
      >
        <router-link to="/account" class="navbar-item">
          <span class="icon"><i class="fas fa-user fa-xs"></i></span> {{$t('account')}}
        </router-link>
        <router-link to="/activity" class="navbar-item">
          <span class="icon"><i class="fas fa-history fa-xs"></i></span> {{$t('activity-history')}}
        </router-link>
        <template v-if="currentUser.admin">
          <a v-if="!currentUser.adminByNow" class="navbar-item" @click="openAdminSession()">
            <span class="icon"><i class="fas fa-star fa-xs"></i></span> {{$t('open-admin-session')}}
          </a>
          <a v-else class="navbar-item" @click="closeAdminSession()">
            <span class="icon"><i class="far fa-star fa-xs"></i></span> {{$t('close-admin-session')}}
          </a>
        </template>
        <hr class="navbar-divider">
        <a class="navbar-item" @click="openHotkeysModal()">
          <span class="icon"><i class="far fa-keyboard fa-xs"></i></span> {{$t('shortcuts')}}
        </a>
        <a class="navbar-item" @click="openAboutModal()">
          <span class="icon"><i class="fas fa-info-circle fa-xs"></i></span> {{$t('about-ims')}}
        </a>
        <hr class="navbar-divider">
        <a class="navbar-item" @click="logout()">
          <span class="icon"><i class="fas fa-power-off fa-xs"></i></span> {{ $t('logout') }}
        </a>
      </navbar-dropdown>
    </div>
  </div>
  <div class="hidden" v-shortkey.once="openHotkeysModalShortcut" @shortkey="openHotkeysModal"></div>
</nav>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {changeLanguageMixin} from '@/lang.js';

import NavbarDropdown from './NavbarDropdown';
import LanguageSelector from './LanguageSelector';
import HotkeysModal from './HotkeysModal';
import AboutImsModal from './AboutImsModal';
import ImsSearcher from '@/components/search/ImsSearcher';
import ProjectSectionSelector from '@/components/project/ProjectSectionSelector';
import ImageName from '@/components/image/ImageName';
import constants from '@/utils/constants.js';
import shortcuts from '@/utils/shortcuts.js';

export default {
  name: 'ims-navbar',
  components: {
    NavbarDropdown,
    LanguageSelector,
    ImsSearcher,
    ProjectSectionSelector,
    ImageName
  },
  mixins: [changeLanguageMixin],
  data() {
    return {
      openedTopMenu: false,
      megaOpen: false,
      hotkeysModal: null,
      appEngineEnabled: constants.APPENGINE_ENABLED,
      aboutModal: null
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    currentProject: get('currentProject/project'),
    // The page label shown as a breadcrumb next to the logo. Main-menu pages use their
    // navItems label; project routes show the project name; account/activity their labels.
    pageTitle() {
      const active = this.navItems.find(item => item.id === this.activeNavId);
      if (active) {
        return active.label;
      }
      const path = this.$route.path;
      if (path.startsWith('/project/') && this.currentProject) {
        return this.currentProject.name;
      }
      if (path.startsWith('/account')) {
        return this.$t('account');
      }
      if (path.startsWith('/activity')) {
        return this.$t('activity-history');
      }
      return '';
    },
    // On a project route the breadcrumb gains a second segment: the section selector,
    // or — inside the image viewer — the active image filename.
    isProjectPage() {
      return this.$route.path.startsWith('/project/') && Boolean(this.currentProject);
    },
    isViewerPage() {
      return this.isProjectPage && this.$route.path.includes('/image/');
    },
    // The image instance currently focused in the viewer (drives the viewer breadcrumb).
    activeImage() {
      const viewer = this.$store.getters['currentProject/currentViewer'];
      if (!viewer || !viewer.images) {
        return null;
      }
      const wrapper = viewer.images[viewer.activeImage] || viewer.images[Object.keys(viewer.images)[0]];
      return wrapper ? wrapper.imageInstance : null;
    },
    // Header shows the first name only, and reflects edits made on the Account page
    // (the editable account model is the source of truth; fall back to the user object).
    userFirstName() {
      return (this.currentAccount && this.currentAccount.firstName)
        || this.currentUser.firstname
        || (this.currentUser.fullName || '').split(/[\s(]/)[0]
        || this.currentUser.name;
    },
    openHotkeysModalShortcut() {
      return shortcuts['general-shortcuts-modal'];
    },
    // ZayaAI mega-menu entries (UI-1). Visible labels are i18n values; keys are unchanged.
    navItems() {
      return [
        {id: 'home', label: this.$t('home'), to: '/', icon: 'fa-home', guard: true},
        {id: 'projects', label: this.$t('projects'), to: '/projects', icon: 'fa-th-large', guard: true},
        {id: 'storage', label: this.$t('storage'), to: '/storage', icon: 'fa-upload', guard: !this.currentUser.guestByNow},
        {id: 'ontologies', label: this.$t('ontologies'), to: '/ontology', icon: 'fa-hashtag', guard: true},
        {id: 'apps', label: this.$t('app-engine.applications'), to: '/apps', icon: 'fa-code', guard: this.appEngineEnabled},
        {id: 'admin', label: this.$t('admin-menu'), to: '/admin', icon: 'fa-wrench', guard: this.currentUser.adminByNow}
      ].filter(item => item.guard);
    },
    // The nav item whose route prefixes the current path (Home matches exactly '/').
    activeNavId() {
      const path = this.$route.path;
      if (path === '/') {
        return 'home';
      }
      const match = this.navItems
        .filter(item => item.to !== '/' && path.startsWith(item.to))
        .sort((a, b) => b.to.length - a.to.length)[0];
      return match ? match.id : null;
    }
  },
  watch: {
    $route() {
      this.openedTopMenu = false;
      this.megaOpen = false;
    }
  },
  methods: {
    // required to use programmatic modal for correct display in IE11
    openHotkeysModal() {
      if (!this.hotkeysModal) {
        this.hotkeysModal = this.$buefy.modal.open({
          parent: this,
          component: HotkeysModal,
          hasModalCard: true,
          onCancel: () => this.hotkeysModal = null,
        });
      }
    },
    openAboutModal() {
      this.$buefy.modal.open({
        parent: this,
        component: AboutImsModal,
        hasModalCard: true
      });
    },
    // ---

    async openAdminSession() {
      try {
        await this.$store.dispatch('currentUser/openAdminSession');
        this.$router.push('/admin');
      } catch (error) {
        console.log(error);
      }
    },
    async closeAdminSession() {
      try {
        await this.$store.dispatch('currentUser/closeAdminSession');
        if (this.$router.currentRoute.path === '/') {
          this.$router.push('/projects');
        } else {
          this.$router.push('/');
        }
      } catch (error) {
        console.log(error);
      }
    },

    async logout() {
      try {
        this.$store.dispatch('logout');
        this.changeLanguage();
        await this.$keycloak.logout();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-logout')});
      }
    }
  }
};
</script>

<style lang="scss">
@import '@/assets/styles/colors.scss';

/* ZayaAI brand wordmark + logo-launched mega-menu (UI-1).
   Visual target: .design-handoff/project/*.html (.brand / .mega*). */
.zaya-brandwrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

/* breadcrumb page title next to the logo (design: ZAYA / <Page>) */
.zaya-crumb {
  display: flex;
  align-items: center;
  height: 100%;
  min-width: 0;
}
.zaya-crumb-sep {
  color: #cfd3da;
  font-size: 18px;
  font-weight: 300;
  margin: 0 8px 0 2px;
}
.zaya-crumb-page {
  font-size: 15px;
  font-weight: 700;
  color: $ink-1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* viewer breadcrumb: the active image filename, kept on one line and truncated */
.zaya-crumb-image {
  max-width: 22rem;
  display: inline-block;
}

/* Responsive: at 150% browser zoom / tablet widths the fixed-width brand + right menu
   overflowed the viewport and grew a horizontal scrollbar (the "platform looks bad" a
   zoomed client sees). The breadcrumb is the flexible part - compress it first. */
@media screen and (max-width: 1400px) {
  .zaya-crumb-image {
    max-width: 12rem;
  }
}

@media screen and (max-width: 1150px) {
  .zaya-crumb-image {
    max-width: 7rem;
  }
  .zaya-crumb-page {
    max-width: 9rem;
  }
}
.zaya-crumb-image .blind-indication {
  font-size: 0.85em;
  font-weight: 600;
}

.zaya-brand {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 16px;
  height: 100%;
  background: none;
  border: 0;
  cursor: pointer;
  text-decoration: none;

  &:hover { background: #f7f8fa; }
  &.open { background: #f2f4f8; }

  .zaya-logo {
    height: 1.75rem;
    width: auto;
    display: block;
  }
  .zaya-caret {
    color: #8a91a1;
    display: inline-flex;
    .fas { padding-right: 0; font-size: 0.75rem; }
  }
}

.zaya-mega {
  position: absolute;
  top: calc(100% + 2px);
  left: 8px;
  width: 520px;
  background: #fff;
  border: 1px solid $line-1;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 17, 22, 0.22);
  padding: 16px 16px 18px;
  z-index: 50;

  h4 {
    margin: 0 0 12px 4px;
    font-size: 13px;
    font-weight: 700;
    color: $ink-1;
  }
}

.zaya-mega-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.zaya-mega-item {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 11px 13px;
  border-radius: 10px;
  border: 1.5px solid transparent;
  cursor: pointer;
  color: $ink-1;
  text-decoration: none;

  &:hover { background: #f2f4f8; }
  &.active { border-color: $primary; }

  .zaya-mega-ico {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 8px;
    background: #edf1f6;
    color: #2c313d;
    display: flex;
    align-items: center;
    justify-content: center;
    .fas { padding-right: 0; }
  }
  &.active .zaya-mega-ico {
    background: $blue-soft;
    color: $primary;
  }
  .zaya-mega-label {
    font-size: 15px;
    font-weight: 600;
  }
}

.navbar {
  font-weight: 600;
  z-index: 500 !important;
  /* UI-7 A1: pure-white top bar per design .topbar (was Bulma is-light grey #f5f5f5):
     white surface, 50px tall, single hairline bottom border, no shadow. */
  background-color: $surface;
  min-height: 50px;
  border-bottom: 1px solid $line-1;
  box-shadow: none;

  .fas, .far {
    padding-right: 0.5rem;
  }
}
</style>
